const NARA_AUTH_STORAGE = 'nara.auth.session';
const NARA_BILLING_API = '/functions/v1/nara-billing';

function naraBillingConfig(root) {
  const url = (root?.dataset.supabaseUrl || '').replace(/\/$/, '');
  const key = root?.dataset.supabaseAnonKey || '';
  return { url, key, ready: Boolean(url && key) };
}

function naraBillingSession() {
  try {
    const raw = window.localStorage.getItem(NARA_AUTH_STORAGE);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

async function naraBillingRequest(config, path, { method = 'GET', body, token } = {}) {
  const headers = {
    apikey: config.key,
    Authorization: `Bearer ${token || config.key}`,
    'Content-Type': 'application/json',
  };
  const response = await fetch(`${config.url}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });
  let payload = null;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }
  return { ok: response.ok, status: response.status, payload };
}

function naraPlanCode(plan, interval) {
  const family = plan === 'complete' ? 'complete' : 'core';
  const period = interval === 'month' || interval === 'monthly' ? 'month' : 'year';
  return `${family}-${period}`;
}

function naraBillingCaptureHash() {
  const raw = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : '';
  if (!raw) return false;
  const params = new URLSearchParams(raw);
  const access = params.get('access_token');
  if (!access) return false;
  window.localStorage.setItem(
    NARA_AUTH_STORAGE,
    JSON.stringify({
      access_token: access,
      refresh_token: params.get('refresh_token'),
      token_type: params.get('token_type') || 'bearer',
      user: null,
    })
  );
  history.replaceState({}, '', window.location.pathname + window.location.search);
  return true;
}

function naraBillingSaveIntent(plan, interval) {
  try {
    window.sessionStorage.setItem(
      'nara.billing.intent',
      JSON.stringify({ plan, interval, search: window.location.search })
    );
  } catch {
    /* ignore */
  }
}

function naraBillingReadIntent() {
  try {
    return JSON.parse(window.sessionStorage.getItem('nara.billing.intent') || '{}');
  } catch {
    return {};
  }
}

function naraFormatDate(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', { dateStyle: 'long' });
}

class NaraBillingCheckout extends HTMLElement {
  connectedCallback() {
    this.config = naraBillingConfig(this);
    this.abort = new AbortController();
    const { signal } = this.abort;
    naraBillingCaptureHash();
    const params = new URLSearchParams(window.location.search);
    const stored = naraBillingReadIntent();
    if (params.has('plan')) {
      this.plan = params.get('plan') === 'complete' ? 'complete' : 'core';
    } else {
      this.plan = stored.plan === 'complete' ? 'complete' : 'core';
    }
    if (params.has('interval')) {
      this.interval = params.get('interval') === 'month' || params.get('interval') === 'monthly' ? 'month' : 'year';
    } else {
      this.interval = stored.interval === 'month' ? 'month' : 'year';
    }
    this.code = naraPlanCode(this.plan, this.interval);
    naraBillingSaveIntent(this.plan, this.interval);
    this.querySelector('[data-nara-billing-login]')?.addEventListener('submit', (event) => this.login(event), { signal });
    this.querySelector('[data-nara-billing-signup]')?.addEventListener('submit', (event) => this.signup(event), { signal });
    this.querySelectorAll('[data-nara-billing-oauth]').forEach((button) => {
      button.addEventListener('click', () => this.oauth(button.dataset.naraBillingOauth), { signal });
    });
    this.querySelector('[data-nara-billing-consent]')?.addEventListener('change', () => this.syncPay(), { signal });
    this.querySelector('[data-nara-billing-pay]')?.addEventListener('click', () => this.startCheckout(), { signal });
    this.boot();
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.checkout?.destroy?.();
  }

  setPanel(name) {
    this.querySelectorAll('[data-nara-billing-panel]').forEach((panel) => {
      panel.hidden = panel.dataset.naraBillingPanel !== name;
    });
  }

  status(message, tone) {
    const el = this.querySelector('[data-nara-billing-status]');
    if (!el) return;
    el.hidden = !message;
    el.textContent = message || '';
    el.dataset.tone = tone || 'info';
  }

  async boot() {
    const { payload } = await naraBillingRequest(
      this.config,
      `${NARA_BILLING_API}?action=quote&plan=${encodeURIComponent(this.code)}`,
      { token: naraBillingSession()?.access_token },
    );
    if (!payload?.ok) {
      this.status('This membership isn’t available right now.', 'error');
      return;
    }
    this.quote = payload;
    this.publishable = '';
    const catalog = await naraBillingRequest(this.config, `${NARA_BILLING_API}?action=catalog`);
    this.publishable = catalog.payload?.publishable_key || this.dataset.stripeKey || '';
    this.renderQuote(payload);
    const session = naraBillingSession();
    if (session?.access_token) {
      const entitlements = await naraBillingRequest(this.config, `${NARA_BILLING_API}?action=entitlements`, {
        token: session.access_token,
      });
      const entitlement = entitlements.payload;
      if (entitlement?.apple_managed || (entitlement?.gift && entitlement.plan !== 'FREE')) {
        this.setPanel('blocked');
        const note = this.querySelector('[data-nara-billing-blocked]');
        if (note) {
          note.textContent = entitlement.apple_managed
            ? 'This account already has an App Store or Google Play membership. Manage billing in the Nara app.'
            : 'A gifted membership is already active on this account.';
        }
        return;
      }
      this.setPanel('billing');
    } else {
      this.setPanel('auth');
    }
    this.syncPay();
  }

  oauth(provider) {
    if (!this.config.ready || (provider !== 'apple' && provider !== 'google')) return;
    naraBillingSaveIntent(this.plan, this.interval);
    const redirectTo = window.location.href.split('#')[0];
    const url = new URL(`${this.config.url}/auth/v1/authorize`);
    url.searchParams.set('provider', provider);
    url.searchParams.set('redirect_to', redirectTo);
    window.location.assign(url.toString());
  }

  renderQuote(quote) {
    this.querySelectorAll('[data-nara-billing-plan]').forEach((el) => {
      el.textContent = quote.plan_label;
    });
    const heading = this.querySelector('[data-nara-billing-heading]');
    if (heading) heading.textContent = quote.plan_label;
    this.querySelectorAll('[data-nara-billing-interval]').forEach((el) => {
      el.textContent = quote.interval_label;
    });
    const features = this.querySelector('[data-nara-billing-features]');
    if (features) {
      features.replaceChildren();
      (quote.features || []).forEach((item) => {
        const li = document.createElement('li');
        li.textContent = item;
        features.append(li);
      });
    }
    this.setText('[data-nara-billing-trial]', quote.trial_eligible ? `${quote.trial_days}-day free trial` : 'No free trial');
    this.setText('[data-nara-billing-today]', quote.due_today_label);
    this.setText('[data-nara-billing-next]', naraFormatDate(quote.next_billing_at));
    this.setText('[data-nara-billing-recurring]', `${quote.amount_label} / ${quote.plan_interval}`);
    this.setText('[data-nara-billing-total]', quote.due_today_label);
    const trialCopy = this.querySelector('[data-nara-billing-trial-copy]');
    if (trialCopy) trialCopy.hidden = !quote.trial_eligible;
    const pay = this.querySelector('[data-nara-billing-pay]');
    if (pay) pay.textContent = quote.trial_eligible ? 'Start 30-day free trial' : 'Subscribe to Nara';
  }

  setText(selector, value) {
    const el = this.querySelector(selector);
    if (el) el.textContent = value || '—';
  }

  syncPay() {
    const consent = this.querySelector('[data-nara-billing-consent]');
    const pay = this.querySelector('[data-nara-billing-pay]');
    if (pay) pay.disabled = !consent?.checked;
  }

  async login(event) {
    event.preventDefault();
    const form = event.target;
    const { ok, payload } = await naraBillingRequest(this.config, '/auth/v1/token?grant_type=password', {
      method: 'POST',
      body: { email: form.elements.email.value.trim(), password: form.elements.password.value },
    });
    if (!ok || !payload?.access_token) {
      this.status('We couldn’t sign you in. Check your details and try again.', 'error');
      return;
    }
    window.localStorage.setItem(NARA_AUTH_STORAGE, JSON.stringify(payload));
    await this.boot();
    this.setPanel('billing');
  }

  async signup(event) {
    event.preventDefault();
    const form = event.target;
    if (form.elements.password.value !== form.elements.confirm.value) {
      this.status('The passwords do not match.', 'error');
      return;
    }
    const { ok, payload } = await naraBillingRequest(this.config, '/auth/v1/signup', {
      method: 'POST',
      body: { email: form.elements.email.value.trim(), password: form.elements.password.value },
    });
    if (!ok || payload?.error || payload?.msg) {
      this.status(payload?.msg || 'We couldn’t create this account. Try again.', 'error');
      return;
    }
    if (payload?.access_token) {
      window.localStorage.setItem(NARA_AUTH_STORAGE, JSON.stringify(payload));
      await this.boot();
      this.setPanel('billing');
      return;
    }
    this.status('Check your email to confirm this account, then return to checkout.', 'success');
  }

  async startCheckout() {
    const consent = this.querySelector('[data-nara-billing-consent]');
    if (!consent?.checked) {
      this.status('Confirm the subscription terms to continue.', 'error');
      return;
    }
    const session = naraBillingSession();
    if (!session?.access_token) {
      this.setPanel('auth');
      return;
    }
    const pay = this.querySelector('[data-nara-billing-pay]');
    if (pay) {
      pay.disabled = true;
      pay.textContent = 'Preparing secure checkout…';
    }
    this.status('');
    const { ok, payload } = await naraBillingRequest(this.config, NARA_BILLING_API, {
      method: 'POST',
      token: session.access_token,
      body: { action: 'checkout', plan: this.code },
    });
    if (!ok || !payload?.client_secret) {
      if (pay) {
        pay.disabled = false;
        this.syncPay();
        if (this.quote) pay.textContent = this.quote.trial_eligible ? 'Start 30-day free trial' : 'Subscribe to Nara';
      }
      this.status(payload?.message || 'Checkout isn’t ready. Try again or manage billing from My Nara.', 'error');
      if (payload?.error === 'store_active' || payload?.error === 'gift_active' || payload?.error === 'already_subscribed') {
        this.setPanel('blocked');
        const note = this.querySelector('[data-nara-billing-blocked]');
        if (note) note.textContent = payload.message || 'This account already has a membership.';
      }
      return;
    }
    if (!this.publishable || !window.Stripe) {
      this.status('Stripe isn’t configured on this website yet.', 'error');
      return;
    }
    const stripe = window.Stripe(this.publishable);
    this.checkout = await stripe.initEmbeddedCheckout({ clientSecret: payload.client_secret });
    const mount = this.querySelector('[data-nara-billing-mount]');
    if (mount) {
      mount.hidden = false;
      this.checkout.mount(mount);
    }
    if (pay) pay.hidden = true;
  }
}

class NaraBillingSuccess extends HTMLElement {
  connectedCallback() {
    this.config = naraBillingConfig(this);
    this.sessionId = new URLSearchParams(window.location.search).get('session_id') || '';
    this.tries = 0;
    this.poll();
  }

  async poll() {
    const session = naraBillingSession();
    if (!session?.access_token || !this.sessionId) {
      this.show('waiting', 'Confirming your membership…');
      return;
    }
    const { payload } = await naraBillingRequest(
      this.config,
      `${NARA_BILLING_API}?action=confirm&session_id=${encodeURIComponent(this.sessionId)}`,
      { token: session.access_token },
    );
    if (payload?.ready && payload.entitlement?.plan && payload.entitlement.plan !== 'FREE') {
      this.renderReady(payload.entitlement);
      return;
    }
    this.tries += 1;
    if (this.tries < 12) {
      this.show('waiting', 'Confirming your membership with Stripe…');
      window.setTimeout(() => this.poll(), 1500);
      return;
    }
    this.show('waiting', 'We’re still confirming your membership. Refresh this page, or open My Nara in a moment.');
  }

  renderReady(entitlement) {
    this.show('ready');
    const plan = this.querySelector('[data-nara-success-plan]');
    const status = this.querySelector('[data-nara-success-status]');
    const next = this.querySelector('[data-nara-success-next]');
    if (plan) plan.textContent = entitlement.plan === 'COMPLETE' ? 'Nara Complete' : 'Nara Core';
    if (status) status.textContent = entitlement.in_trial ? '30-day free trial' : 'Active';
    if (next) next.textContent = naraFormatDate(entitlement.current_period_end || entitlement.trial_ends_at);
  }

  show(name, message) {
    this.querySelectorAll('[data-nara-success-panel]').forEach((panel) => {
      panel.hidden = panel.dataset.naraSuccessPanel !== name;
    });
    if (message) {
      const status = this.querySelector('[data-nara-success-waiting]');
      if (status) status.textContent = message;
    }
  }
}

class NaraBillingManage extends HTMLElement {
  connectedCallback() {
    this.config = naraBillingConfig(this);
    this.querySelector('[data-nara-billing-portal]')?.addEventListener('click', () => this.openPortal());
    this.querySelector('[data-nara-billing-subscribe]')?.addEventListener('click', () => {
      window.location.assign('/pages/membership');
    });
  }

  async openPortal() {
    const session = naraBillingSession();
    if (!session?.access_token) {
      window.location.assign('/pages/login?next=/pages/my-nara');
      return;
    }
    const button = this.querySelector('[data-nara-billing-portal]');
    if (button) button.disabled = true;
    const { ok, payload } = await naraBillingRequest(this.config, NARA_BILLING_API, {
      method: 'POST',
      token: session.access_token,
      body: { action: 'portal' },
    });
    if (ok && payload?.url) {
      window.location.assign(payload.url);
      return;
    }
    if (button) button.disabled = false;
    const note = this.querySelector('[data-nara-billing-portal-status]');
    if (note) {
      note.hidden = false;
      note.textContent = 'Billing portal isn’t available yet. If you subscribe through Apple, manage it in the Nara app.';
    }
  }
}

if (!customElements.get('nara-billing-checkout')) customElements.define('nara-billing-checkout', NaraBillingCheckout);
if (!customElements.get('nara-billing-success')) customElements.define('nara-billing-success', NaraBillingSuccess);
if (!customElements.get('nara-billing-manage')) customElements.define('nara-billing-manage', NaraBillingManage);
